import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Wallet, TrendingUp, FileText, ArrowLeftRight, BarChart3, LifeBuoy, CalendarDays } from "lucide-react";
import API from "@/lib/api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: ${(p.value || 0).toLocaleString()}</p>
      ))}
    </div>
  );
};

export default function InvestorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/dashboard/stats").then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const chartData = stats.chart_data || [];
  const fmt = v => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const actions = [
    { label: "View Documents", icon: FileText, path: "/investor/documents", color: "text-blue-400" },
    { label: "Transactions", icon: ArrowLeftRight, path: "/investor/transactions", color: "text-emerald-400" },
    { label: "Reports", icon: BarChart3, path: "/investor/reporting", color: "text-amber-400" },
    { label: "Support", icon: LifeBuoy, path: "/investor/support", color: "text-violet-400" },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="investor-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Welcome, {user?.name}</h1>
        <p className="text-muted-foreground text-sm mt-1">{stats.fund_name ? `${stats.fund_name} — ` : ""}Your investment portfolio overview</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card bg-blue-500/10 border-blue-500/20" data-testid="stat-portfolio-value">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Portfolio Value</span>
              <Wallet className="w-4 h-4 text-blue-500 dark:text-blue-400" strokeWidth={1.5} />
            </div>
            <p className="font-heading text-2xl font-bold">
              {loading ? <span className="inline-block w-24 h-7 bg-secondary rounded animate-pulse" /> : fmt(stats.portfolio_value)}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card bg-emerald-500/10 border-emerald-500/20" data-testid="stat-total-returns">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total Returns</span>
              <TrendingUp className="w-4 h-4 text-emerald-500 dark:text-emerald-400" strokeWidth={1.5} />
            </div>
            <p className="font-heading text-2xl font-bold">
              {loading ? <span className="inline-block w-16 h-7 bg-secondary rounded animate-pulse" /> : (
                <span className={stats.total_returns >= 0 ? "text-emerald-400" : "text-red-400"}>{stats.total_returns?.toFixed(2)}%</span>
              )}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card bg-amber-500/10 border-amber-500/20" data-testid="stat-ytd-return">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">YTD Return</span>
              <CalendarDays className="w-4 h-4 text-amber-500 dark:text-amber-400" strokeWidth={1.5} />
            </div>
            <p className="font-heading text-2xl font-bold">
              {loading ? <span className="inline-block w-16 h-7 bg-secondary rounded animate-pulse" /> : (
                <span className={stats.ytd_return >= 0 ? "text-emerald-400" : "text-red-400"}>{stats.ytd_return?.toFixed(2)}%</span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Portfolio Value Chart */}
      {chartData.length > 0 && (
        <Card data-testid="portfolio-chart" className="mt-4">
          <CardHeader className="p-5 pb-2">
            <CardTitle className="font-heading text-base">Portfolio Value Over Time</CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-2">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7c5cc4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#7c5cc4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="value" name="Portfolio" stroke="#7c5cc4" strokeWidth={2} fill="url(#colorVal)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Net Income by Period */}
      {chartData.length > 0 && (
        <Card data-testid="income-chart" className="mt-4">
          <CardHeader className="p-5 pb-2">
            <CardTitle className="font-heading text-base">Net Income by Period</CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-2">
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="income" name="Net Income" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card data-testid="investor-quick-actions">
        <CardHeader className="p-5 pb-2">
          <CardTitle className="font-heading text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {actions.map((a) => (
            <Button key={a.path} asChild variant="secondary" className="justify-start border border-border h-10 text-sm">
              <Link to={a.path}><a.icon className={`w-4 h-4 mr-3 ${a.color}`} /> {a.label}</Link>
            </Button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
